import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";

const Navigation = () => {
  const { isAuthenticated, isAdmin, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const isPortalArea = location.pathname.startsWith("/portal") || location.pathname.startsWith("/artists");
  const isAdminArea = location.pathname.startsWith("/admin");

  let navLinks;
  if (isAdminArea && isAdmin) {
    navLinks = [
      { label: "Dashboard", path: "/admin" },
      { label: "Add Artist", path: "/admin/artists/new" },
      { label: "Gallery", path: "/admin/gallery" },
      { label: "Projects", path: "/admin/projects" },
      { label: "Bookings", path: "/admin/bookings" },
      { label: "Users", path: "/admin/users" },
    ];
  } else if (isPortalArea && isAuthenticated) {
    navLinks = [
      { label: "Portal", path: "/portal" },
      { label: "Artists", path: "/portal" },
      { label: "Projects", path: "/projects" },
      { label: "Contact", path: "/contact" },
    ];
  } else {
    navLinks = [
      { label: "Home", path: "/" },
      { label: "Artists", path: isAuthenticated ? "/portal" : "/login" },
      { label: "Projects", path: "/projects" },
      { label: "About", path: "/#about" },
      { label: "Contact", path: "/contact" },
    ];
  }

  const go = (path) => {
    setMenuOpen(false);
    if (path.startsWith("/#")) {
      if (location.pathname !== "/") navigate("/");
      setTimeout(() => {
        const id = path.split("#")[1];
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen ? "glass-dark py-3" : "bg-transparent py-5"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => go("/")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src="/images/mrva-logo-white.png"
            alt="MRVA Productions"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <button key={l.label} onClick={() => go(l.path)} className="nav-link" data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </button>
          ))}
        </div>

        {/* Right */}
        <div className="hidden md:flex items-center gap-4">
          {isAuthenticated ? (
            <>
              {isAdmin && !isAdminArea && (
                <button onClick={() => go("/admin")} className="nav-link" data-testid="nav-admin">Admin</button>
              )}
              {!isAdmin && isAdminArea ? null : null}
              <span className="font-body text-white/50 text-[0.65rem] tracking-widest uppercase">
                {user?.name}
              </span>
              <button
                onClick={handleLogout}
                className="btn-outline-gold text-xs px-5 py-2 rounded-sm"
                data-testid="nav-logout"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button onClick={() => go("/login")} className="nav-link" data-testid="nav-login">Client Login</button>
              <button
                onClick={() => go("/signup")}
                className="btn-gold text-xs px-6 py-2.5 rounded-sm"
                data-testid="nav-signup-cta"
              >
                Get Started
              </button>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          data-testid="nav-hamburger"
        >
          {menuOpen ? <X size={22} className="text-gold" /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          menuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pt-4 pb-6 flex flex-col gap-4 border-t border-gold/10 mt-3">
          {navLinks.map((l) => (
            <button key={l.label} onClick={() => go(l.path)} className="nav-link text-left">
              {l.label}
            </button>
          ))}
          <div className="gold-divider my-2" />
          {isAuthenticated ? (
            <>
              {isAdmin && !isAdminArea && (
                <button onClick={() => go("/admin")} className="nav-link text-left">Admin</button>
              )}
              <button
                onClick={handleLogout}
                className="btn-outline-gold text-xs px-5 py-2.5 rounded-sm w-fit"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button onClick={() => go("/login")} className="nav-link text-left">Client Login</button>
              <button
                onClick={() => go("/signup")}
                className="btn-gold text-xs px-6 py-2.5 rounded-sm w-fit"
              >
                Get Started
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
